/* ==========================================================================
   LSC - Collage Collection: photo parallax (created 15 Oct 2026;
   v11 16 Oct 2026: will-change only near the screen)
   assets/lsc-collage-collection.js

   Loaded by sections/lsc-collage-collection.liquid only when "Photo
   parallax" is above 0 (and always in the theme editor, so the setting can
   be raised live). Adapted from assets/lsc-product-of-the-day-parallax.js.

   WHAT IT DOES
   Sets ONE custom property on each collage, --lsc-cc-p, from -0.5 (the
   collage is just entering at the bottom of the screen) to +0.5 (just
   leaving at the top). Everything else happens in the CSS
   (assets/lsc-collage-collection.css, "Parallax"): each photo is drawn
   taller than its tile and moved by p x k x depth x its height, where
   depth differs per tile so the collage reads as layered.

   HOW IT STAYS CHEAP
   - Only collages near the viewport are updated (IntersectionObserver with
     a 200px margin). A collection page can have 24-50 rows; usually 1-3
     are on screen.
   - One requestAnimationFrame per frame at most, however many scroll
     events arrive.
   - One getBoundingClientRect per visible collage per frame, and one style
     write each. No layout is read after a write within the same frame.
   - LSC - v11 (16 Oct 2026, audit fix 6): the GPU layer hint
     (will-change: translate on the photos) is only given to collages near
     the viewport: this script adds .is-lsc-near to a collage while the
     IntersectionObserver sees it and removes it when it leaves (the CSS
     does the rest). v4-v10 put the hint on every photo, about 96 layers.

   ACCESSIBILITY
   Visitors with "reduce motion" on get no movement: the script clears the
   property and the CSS drops the enlargement.

   THEME EDITOR AND FILTERS
   Re-rendered sections don't re-run scripts, so the module re-scans on
   shopify:section:load and tidies up on shopify:section:unload. It also
   re-scans on the theme's collection:reloaded event, fired after the
   filter drawer swaps in new rows. Loading the file twice (two sections)
   just re-scans: listeners are added once.
   ========================================================================== */
(function () {
  // Already defined by an earlier instance of the section: just re-scan.
  if (window.LscCcParallax) {
    window.LscCcParallax.scan();
    return;
  }

  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var tracked = []; // collages in sections with parallax > 0
  var visible = new Set(); // the subset currently near the viewport
  var queued = false;
  var NEAR = 'is-lsc-near'; // v11: class for collages near the viewport

  // Without IntersectionObserver (very old browsers) every tracked collage
  // updates on each frame.
  var io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) visible.add(entry.target);
              else visible.delete(entry.target);
              // LSC - v11: will-change only near the screen (see the header).
              entry.target.classList.toggle(NEAR, entry.isIntersecting);
            });
            queue();
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  /** Parallax amount of the section a collage belongs to (0 - 0.3). */
  function amountOf(el) {
    var root = el.closest('[data-lsc-cc]');
    return root ? parseFloat(root.getAttribute('data-lsc-cc-parallax')) || 0 : 0;
  }

  function motionOff() {
    return !!(reduceMotion && reduceMotion.matches);
  }

  /** Measure all visible collages, then write, so layout is read once. */
  function tick() {
    queued = false;
    var list = io ? Array.from(visible) : tracked;
    if (motionOff()) {
      list.forEach(function (el) {
        el.style.removeProperty('--lsc-cc-p');
      });
      return;
    }
    var viewport = window.innerHeight || document.documentElement.clientHeight;
    // Read phase.
    var values = list.map(function (el) {
      var rect = el.getBoundingClientRect();
      // 0 = just entering at the bottom, 1 = just leaving at the top.
      var progress = (viewport - rect.top) / (viewport + rect.height);
      progress = Math.min(1, Math.max(0, progress));
      return (progress - 0.5).toFixed(4);
    });
    // Write phase.
    list.forEach(function (el, i) {
      el.style.setProperty('--lsc-cc-p', values[i]);
    });
  }

  function queue() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(tick);
  }

  /** Pick up new collages and forget removed ones (theme editor). */
  function scan() {
    tracked = tracked.filter(function (el) {
      if (el.isConnected) return true;
      if (io) io.unobserve(el);
      visible.delete(el);
      el.classList.remove(NEAR);
      return false;
    });
    document.querySelectorAll('[data-lsc-cc-collage]').forEach(function (el) {
      if (!amountOf(el)) {
        // Setting at 0: clear anything left from an earlier value, and stop
        // tracking it (theme editor: the amount was just lowered to 0).
        el.style.removeProperty('--lsc-cc-p');
        el.classList.remove(NEAR);
        var at = tracked.indexOf(el);
        if (at !== -1) {
          tracked.splice(at, 1);
          if (io) io.unobserve(el);
          visible.delete(el);
        }
        return;
      }
      if (tracked.indexOf(el) === -1) {
        tracked.push(el);
        if (io) io.observe(el);
        else el.classList.add(NEAR); // no observer: every collage moves
      }
    });
    queue();
  }

  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue);
  if (reduceMotion && reduceMotion.addEventListener) {
    reduceMotion.addEventListener('change', function () {
      // Turned on: clear every collage (not only the visible ones), so none
      // keeps an offset. Turned off: the next frame positions visible ones.
      if (motionOff()) {
        tracked.forEach(function (el) {
          el.style.removeProperty('--lsc-cc-p');
        });
      }
      queue();
    });
  }
  document.addEventListener('shopify:section:load', scan);
  // LSC - v2 (15 Oct 2026): the theme's filter drawer (assets/collection.js)
  // replaces the rows' container after each filter change and then fires
  // "collection:reloaded". The new collages need picking up, and the old
  // ones dropping, exactly as after a theme-editor reload.
  document.addEventListener('collection:reloaded', scan);
  document.addEventListener('shopify:section:unload', function () {
    // The section is removed after this event fires; tidy up next frame.
    window.requestAnimationFrame(scan);
  });

  window.LscCcParallax = { scan: scan };
  scan();
})();
